import { ChangeEvent, DragEvent, FC, useState } from 'react';
import styled, { css } from 'styled-components';

import { ReactComponent as ImgIcon } from '@/assets/icons/img-placeholder.svg';
import { InnerLabel, Notice, Title } from '@/components/Forms/FileField';
import { SrOnly } from '@/components/Layout/Parts';
import useAlerts from '@/hooks/useAlerts';

import { ImageData } from './VariationOne';

const Dropzone = styled.div<{ $dragging: boolean }>`
  border-radius: 4px;
  border: 1px dashed ${({ theme }) => theme.colors.lightGray};
  color: ${({ theme }) => theme.colors.gray};
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 42px;
  height: 116px;
  ${({ theme, $dragging }) =>
    $dragging &&
    css`
      border-color: ${theme.colors.purpleLight};
    `}
`;

interface Props {
  className?: string;
  isInModal?: boolean;
  onClose?: () => void;
  setImages: React.Dispatch<React.SetStateAction<ImageData[]>>;
  noneDivider?: boolean;
  coverIndex: number;
}

const UploadSingleImage: FC<Props> = ({
  setImages,
  isInModal,
  onClose,
  className,
  noneDivider,
  coverIndex,
}) => {
  const [dragging, setDragging] = useState(false);
  const alerts = useAlerts();

  const validateFilesType = (files: FileList): boolean => {
    for (let i = 0; i < files.length; i++) {
      if (
        !['image/png', 'image/jpg', 'image/jpeg', 'image/webp'].includes(
          files[i].type,
        )
      ) {
        alerts.error(
          'Invalid file type!',
          'Please use an image of type png, jpg, or jpeg',
        );
        return false;
      }
    }
    return true;
  };

  const processFiles = (files: FileList) => {
    const newFiles = Array.from(files).map((file) => ({
      image: file,
      text: '',
      textPosition: 'bottom',
      actualPhoto: 'none',
      hasMargins: true,
      quotes: true,
      brakes: false,
      divider: noneDivider ? 'none' : 'white',
    }));

    setImages(() => [newFiles[0]]);
  };

  const onChange = (e: ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && validateFilesType(files)) {
      processFiles(files);
      isInModal && onClose && onClose();
    }
  };

  const handleDrag = (e: DragEvent, isDragging: boolean) => {
    e.preventDefault();
    e.stopPropagation();
    setDragging(isDragging);
  };

  const handleDrop = (e: DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragging(false);

    if (e.dataTransfer?.files && validateFilesType(e.dataTransfer.files)) {
      processFiles(e.dataTransfer.files);
      isInModal && onClose && onClose();
    }
  };

  return (
    <Dropzone
      onDragEnter={(e) => handleDrag(e, true)}
      onDragOver={(e) => handleDrag(e, true)}
      onDragLeave={(e) => handleDrag(e, false)}
      onDrop={handleDrop}
      $dragging={dragging}
      className={className}
    >
      <ImgIcon />
      <Title $type="smallBold" $color="gray" as="div">
        Drop images here or{' '}
        <InnerLabel
          as="label"
          $type="smallBold"
          $color="purple"
          htmlFor={`images-input-${coverIndex}`}
        >
          browse
          <SrOnly
            as="input"
            type="file"
            id={`images-input-${coverIndex}`}
            accept="image/*"
            multiple={false}
            onChange={onChange}
          />
        </InnerLabel>
      </Title>
      <Notice $type="extraSmall" $color="gray" as="div">
        Supports: JPG, JPGS, PNG, WEBP
      </Notice>
    </Dropzone>
  );
};

export default UploadSingleImage;
